import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faArrowLeft, 
  faTrashAlt, 
  faCalendarAlt,
  faUser,
  faFileAlt,
  faDownload,
  faEye,
  faLayerGroup,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import { ADMIN_URLS } from '../../config/apiConfig';

function ViewResource() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchResourceDetails();
  }, [id]);

  const fetchResourceDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminService.getResource(id);
      setResource(response);
    } catch (err) {
      console.error("Error fetching resource details:", err);
      setError("Failed to load resource details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await adminService.deleteResource(id);
      navigate('/admin/resources', { replace: true });
    } catch (err) {
      console.error("Error deleting resource:", err);
      setError("Failed to delete resource. Please try again.");
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFileTypeIcon = (resourceType) => {
    switch (resourceType?.toLowerCase()) {
      case 'document':
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#dc3545' }} />;
      case 'presentation':
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#fd7e14' }} />;
      default:
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#6c757d' }} />;
    }
  };

  // Update the styling for a cleaner look
  const sectionStyle = {
    marginBottom: '1.5rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  };

  const sectionHeaderStyle = {
    padding: '1rem',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const sectionBodyStyle = {
    padding: '1rem'
  };

  const detailItemStyle = {
    marginBottom: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    color: '#6c757d',
    fontWeight: '500'
  };

  const valueStyle = {
    fontSize: '1rem',
    color: '#212529',
    fontWeight: '400',
    padding: '0.5rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading resource details...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <h1 className="admin-page-title">Resource Details</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/admin/resources" className="admin-btn admin-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Resources
            </Link>
            <Link to={`/admin/resources/${id}/edit`} className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
              <FontAwesomeIcon icon={faEdit} />
              Edit
            </Link>
            <button 
              className="admin-btn admin-btn-danger"
              onClick={handleDelete}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
              Delete
            </button>
          </div>
        </div>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/resources" className="admin-breadcrumb-link">Resources</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Resource Details</span>
          </li>
        </ul>
      </div>

      {resource && (
        <div className="admin-content-container">
          {/* Title & Status Section */}
          <div style={sectionStyle}>
            <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: '0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {getFileTypeIcon(resource.resource_type)}
                {resource.title}
              </h2>
              <div>
                {resource.is_public ? (
                  <span style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    <FontAwesomeIcon icon={faEye} style={{ marginRight: '0.35rem' }} />
                    Public
                  </span>
                ) : (
                  <span style={{
                    backgroundColor: '#cce5ff',
                    color: '#004085',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.35rem' }} />
                    Members Only
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info Section - Modified to remove category */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <FontAwesomeIcon icon={faInfoCircle} />
              <h3 style={{ margin: '0', fontSize: '1.1rem' }}>Basic Information</h3>
            </div>
            <div style={sectionBodyStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
                <div style={detailItemStyle}>
                  <div style={labelStyle}>Resource Type</div>
                  <div style={valueStyle}>{resource.resource_type}</div>
                </div>
                <div style={detailItemStyle}>
                  <div style={labelStyle}>Created Date</div>
                  <div style={valueStyle}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '0.5rem' }} />
                    {formatDate(resource.created_at)}
                  </div>
                </div>
                <div style={detailItemStyle}>
                  <div style={labelStyle}>Last Updated</div>
                  <div style={valueStyle}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '0.5rem' }} />
                    {formatDate(resource.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Associated Program Section */}
          {resource.program_id && (
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <FontAwesomeIcon icon={faLayerGroup} />
                <h3 style={{ margin: '0', fontSize: '1.1rem' }}>Associated Program</h3>
              </div>
              <div style={sectionBodyStyle}>
                <div style={detailItemStyle}>
                  <div style={labelStyle}>Program</div>
                  <div style={valueStyle}>
                    <Link to={`/admin/programs/${resource.program_id}`} style={{ 
                      color: 'var(--admin-primary)',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}>
                      {resource.program_title}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resource File Section */}
          {resource.url && (
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <FontAwesomeIcon icon={faDownload} />
                <h3 style={{ margin: '0', fontSize: '1.1rem' }}>Resource File</h3>
              </div>
              <div style={sectionBodyStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ ...detailItemStyle, flex: '1 1 60%', marginBottom: 0 }}>
                    <div style={labelStyle}>URL/Path</div>
                    <div style={valueStyle}>{resource.url}</div>
                  </div>
                  <div style={{ alignSelf: 'flex-end' }}>
                    <a 
                      href={resource.url.startsWith('http') ? resource.url : `${process.env.REACT_APP_API_URL || ''}${resource.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-btn admin-btn-success"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete this resource? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ViewResource;
